// src/components/Procurement/AllocationEditor.jsx
// Cash-application editor (db/275). Given ONE settlement leg (a supplier payment / credit), shows how
// it is split across the supplier's open orders, lets the user edit the split, and saves it as a
// stored fact (POST /api/payment-allocations reconciles to the target via apply/release events).
// Replaces the misleading FIFO "settled oldest first" preview — the split no longer wobbles.
//
// Props:
//   settlementLegId  (string, required) — the finance id of the payment/credit leg
//   readOnly         (bool)   — show the split without edit controls
//   onSaved          (fn)     — called after a successful save (refresh parent)
//   compact          (bool)   — tighter styling for embedding in a row
import MoneyTextInput from '../MoneyTextInput';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../../services/api';

import { Tx } from '../GhostBits';
import { isMissingRoute } from '../../lib/missingRoute.mjs';
import { useTranslation } from 'react-i18next';
let PREVIEW_MISSING = false;   // the E4 preview route is absent on this API: stop asking for the session
const fmt = (n) => (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
const r2 = (n) => Math.round(n * 100) / 100;
// Distribute each order's entered amount across its legs (fill each leg to its own capacity): the body of the write AND of the served preview.
function buildAllocations(orders, amounts) {
  const allocations = [];
  for (const g of orders) {
    let amt = parseFloat(amounts[g.key]) || 0;
    for (const leg of g.legs) {
      const cap = r2(leg.open + leg.allocated_here);
      const put = Math.min(cap, amt);
      if (put > 0.005) allocations.push({ invoice_leg_id: leg.invoice_leg_id, amount: r2(put) });
      amt = r2(amt - put);
    }
  }
  return allocations;
}

export default function AllocationEditor({ settlementLegId, readOnly = false, onSaved, compact = false, operationId = null }) {
  const [data, setData]     = useState(null);   // { settlement, unallocated, open_obligations }
  const [amounts, setAmounts] = useState({});    // operation_id (group key) -> string (editable)
  const [saving, setSaving] = useState(false);
  // The settlement endpoint has no idempotency key.  State disables the button after render; this
  // synchronous lock also closes the small double-click window before that render happens.
  const saveInFlight = useRef(false);
  const [msg, setMsg]       = useState(null);
  const { t, i18n } = useTranslation('orderPage');
  const [pv, setPv] = useState(null);   // E4 (47007 W7): the SERVED preview of the typed split (POST /payment-allocations/preview); null on an API without it -> the client figures below stay

  const load = useCallback(() => {
    if (!settlementLegId) return;
    setMsg(null);
    api.request(`/payment-allocations?settlement_leg_id=${settlementLegId}`)
      .then(d => setData(d))
      .catch(e => setMsg({ type: 'err', text: e.message || 'Failed to load allocation' }));
  }, [settlementLegId]);

  useEffect(() => { load(); }, [load]);

  // Group obligation legs by ORDER (an order can have several legs — goods + landed cost). One editable
  // row per order; on save we distribute the entered amount across that order's legs.
  const orders = useMemo(() => {
    const m = new Map();
    for (const o of (data?.open_obligations || [])) {
      if (operationId && o.operation_id !== operationId) continue;
      const key = o.operation_id || o.invoice_leg_id;
      if (!m.has(key)) m.set(key, { key, operation_id: o.operation_id, date: o.date, legs: [], open: 0, allocatedHere: 0, hasLc: false, hasGoods: false });
      const g = m.get(key);
      g.legs.push(o); g.open = r2(g.open + o.open); g.allocatedHere = r2(g.allocatedHere + o.allocated_here);
      if (o.kind === 'lc') g.hasLc = true; else g.hasGoods = true;
    }
    return [...m.values()].sort((a, b) => String(a.operation_id || '').localeCompare(String(b.operation_id || '')));
  }, [data, operationId]);

  useEffect(() => {
    const seed = {};
    for (const g of orders) seed[g.key] = String(g.allocatedHere || 0);
    setAmounts(seed);
  }, [orders]);

  const sig = data && Object.keys(amounts).length ? JSON.stringify(buildAllocations(orders, amounts)) : '';
  useEffect(() => {   // E4: the served before / after of THIS split (writes nothing); a 404 / any failure leaves the client figures in place
    if (!sig || PREVIEW_MISSING) { setPv(null); return undefined; }
    let live = true;
    const h = setTimeout(() => {
      Promise.resolve(api.allocationPreview({ settlement_leg_id: settlementLegId, allocations: JSON.parse(sig) })).then(r => {
        if (!live) return;
        if (isMissingRoute(r)) { PREVIEW_MISSING = true; setPv(null); } else setPv(r && r.ok && r.data && r.data.payment ? r.data : null);
      }).catch(() => live && setPv(null));
    }, 350);
    return () => { live = false; clearTimeout(h); };
  }, [sig, settlementLegId]);

  if (!data) {
    // A failed/rejected load (e.g. a 422 "this leg does not settle a supplier/customer account" for an
    // order-less transfer) must SURFACE — not sit on the spinner forever. Show the error + a retry.
    if (msg?.type === 'err') return (
      <div style={{ fontSize: 11, color: '#b14a30', padding: '6px 0' }}>
        {msg.text || 'Failed to load allocation.'}{' '}
        <button type="button" onClick={load} className="btn sm"><Tx k="ghost.retry" /></button>
      </div>
    );
    return <div style={{ fontSize: 11, color: 'var(--muted)', padding: '6px 0' }}>Loading allocation…</div>;
  }

  const total = Number(data.settlement?.amount) || 0;
  const entered = Object.values(amounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const unallocated = pv ? Number(pv.payment.unallocated_after) : r2(total - entered);   // served when the preview answered
  const over = pv ? pv.allowed === false : unallocated < -0.005;
  const refusals = pv && pv.allowed === false ? (pv.refusals || []) : [];

  const setAmt = (key, v) => setAmounts(a => ({ ...a, [key]: v.replace(/[^0-9.]/g, '') }));
  const fillOpen = (g) => setAmt(g.key, String(r2(g.open + g.allocatedHere)));

  const save = async () => {
    if (saveInFlight.current) return;
    if (over) { setMsg({ type: 'err', text: 'Allocated more than the payment amount.' }); return; }
    saveInFlight.current = true;
    setSaving(true); setMsg(null);
    try {
      const allocations = buildAllocations(orders, amounts);
      const res = await api.request('/payment-allocations', { method: 'POST', body: JSON.stringify({ settlement_leg_id: settlementLegId, allocations }) });
      setMsg({ type: 'ok', text: `Saved · ${res.events_written} change${res.events_written === 1 ? '' : 's'} · ${fmt(res.unallocated)} on account` });
      load();
      if (onSaved) onSaved(res);
    } catch (e) { setMsg({ type: 'err', text: e.message || 'Save failed' }); }
    finally { saveInFlight.current = false; setSaving(false); }
  };

  const cell = { fontSize: 12, padding: compact ? '3px 8px' : '5px 10px', fontVariantNumeric: 'tabular-nums' };
  const inp = { width: 90, textAlign: 'right', fontSize: 12, padding: '3px 6px', border: '1px solid var(--line)', borderRadius: 5, fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 6, background: '#fbfdfb', padding: compact ? '8px 10px' : '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink)' }}>
          Apply this payment ({fmt(total)} F) to orders
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: over ? '#b14a30' : unallocated > 0.005 ? '#c47d10' : '#16a34a' }}>
          {over ? (pv ? 'Not allowed' : `Over by ${fmt(-unallocated)}`) : `On account: ${fmt(unallocated)} F`}
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>
            <th style={{ ...cell, textAlign: 'left' }}>Order</th>
            <th style={{ ...cell, textAlign: 'right' }}>Open</th>
            <th style={{ ...cell, textAlign: 'right' }}>Applied</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr><td colSpan={3} style={{ ...cell, color: 'var(--muted)' }}>No open obligations in this scope — this payment sits fully on account.</td></tr>
          )}
          {orders.map(g => (
            <tr key={g.key} style={{ borderTop: '1px solid var(--line2)' }}>
              <td style={{ ...cell, textAlign: 'left', color: 'var(--muted)' }}>
                {g.operation_id || '—'}
                {g.hasLc && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#c47d10', background: '#fdf3e2', padding: '2px 9px', borderRadius: 9999 }}>{g.hasGoods ? '+LC' : 'LC'}</span>}
              </td>
              <td style={{ ...cell, textAlign: 'right' }}>{fmt(g.open)}</td>
              <td style={{ ...cell, textAlign: 'right' }}>
                {readOnly ? fmt(amounts[g.key]) : (
                  <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                    <MoneyTextInput dense value={amounts[g.key] ?? ''} onChange={e => setAmt(g.key, e.target.value)} style={inp} inputMode="decimal" />
                    <button type="button" onClick={() => fillOpen(g)} title="Fill remaining open"
                      style={{ fontSize: 10, border: '1px solid var(--line)', background: '#fff', borderRadius: 4, padding: '2px 5px', cursor: 'pointer' }}>max</button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* GES-18: the append-only apply/release trail for THIS leg, inline where the user re-allocates, so a
          release they just caused (reduce -> pool) is visible in context. Read-only surfacing of data.events. */}
      {(data.events && data.events.length > 0) && (
        <div style={{ marginTop: 8, borderTop: '1px dashed var(--line2)', paddingTop: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>
            Allocation activity · apply / release (append-only)
          </div>
          <div style={{ maxHeight: 132, overflowY: 'auto' }}>
            {[...data.events].reverse().map((ev, i) => {
              const rel = ev.event_type === 'release';
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', ...cell, padding: '2px 0' }}>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: rel ? '#c47d10' : '#16a34a', background: rel ? '#fdf3e2' : '#e9f7ef', padding: '2px 9px', borderRadius: 9999 }}>{rel ? 'Released' : 'Applied'}</span>
                    <span style={{ color: 'var(--muted)' }}>{ev.operation_id || (ev.invoice_leg_id ? String(ev.invoice_leg_id).slice(0, 8) : '\u2014')}</span>
                  </span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{rel ? '\u2212' : '+'}{fmt(ev.amount)} F</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {refusals.map((r, i) => <div key={i} role="alert" data-testid="alloc-refusal" style={{ fontSize: 11, marginTop: 4, color: '#b14a30' }}><b>{r.code || r}</b> {t('reason.' + (r.code || r), { defaultValue: (i18n.language.startsWith('fr') && r.error_fr) || r.error || '' })}</div>)}
      {msg && <div style={{ fontSize: 11, marginTop: 6, color: msg.type === 'err' ? '#b14a30' : '#16a34a' }}>{msg.text}</div>}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="button" onClick={save} disabled={saving || over}
            style={{ fontSize: 11, background: over ? '#c9c9c9' : '#1b4332', color: '#fff', border: '1px solid transparent', borderRadius: 5, padding: '4px 12px', cursor: over ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {saving ? 'Saving…' : 'Save allocation'}
          </button>
          <button type="button" onClick={load} disabled={saving}
            style={{ fontSize: 11, background: '#fff', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 5, padding: '4px 12px', cursor: 'pointer' }}>
            Reset
          </button>
          <button type="button" onClick={() => setAmounts(Object.fromEntries(orders.map(g => [g.key, '0'])))} disabled={saving}
            style={{ fontSize: 11, background: '#fff', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 5, padding: '4px 12px', cursor: 'pointer' }}>
            Leave on account
          </button>
        </div>
      )}
    </div>
  );
}
